<template>
  <div class="notes-view">
    <!-- Back header -->
    <div class="view-header">
      <button class="back-btn" @click="$emit('back')">
        <i class="tim-icons icon-minimal-left"></i>
      </button>
      <h5 class="view-title mb-0">Notes</h5>
    </div>

    <div class="view-body">
      <!-- Notes list -->
      <div v-if="notes.length > 0" class="notes-list">
        <div
          v-for="(n, i) in sortedNotes"
          :key="'N' + i"
          class="note-card"
          :class="{ 'note-card--important': n.is_important }"
        >
          <p class="note-card__content">{{ n.note }}</p>
          <div class="note-card__footer">
            <div class="note-card__meta">
              <span class="note-card__author" v-if="n.first_name || n.last_name">
                {{ n.first_name }} {{ n.last_name }}
              </span>
              <span class="note-card__date text-muted">
                {{ getLocalDate(n.timestamp) }} at {{ getLocalTime(n.timestamp) }}
              </span>
            </div>
            <div class="note-card__actions" v-if="canManage && n.id">
              <el-tooltip
                v-if="!n.is_important"
                content="Mark as important"
                effect="light"
                :open-delay="300"
                placement="top"
              >
                <button
                  class="note-card__action-btn"
                  @click="toggleImportant(n)"
                >
                  <i class="el-icon-star-off"></i>
                </button>
              </el-tooltip>
              <button
                v-else
                class="note-card__action-btn note-card__action-btn--active"
                disabled
              >
                <i class="el-icon-star-on"></i>
              </button>
              <el-tooltip content="Delete note" effect="light" :open-delay="300" placement="top">
                <button class="note-card__action-btn note-card__action-btn--delete" @click="deleteNote(n)">
                  <i class="el-icon-delete"></i>
                </button>
              </el-tooltip>
            </div>
          </div>
        </div>
      </div>

      <!-- Add note -->
      <div v-if="canManage" class="add-note-section" :class="{ 'no-border': notes.length === 0 }">
        <label class="note-input-label">Add a note</label>
        <textarea
          v-model="newNote"
          class="note-textarea"
          rows="3"
          placeholder="Type your note here..."
        ></textarea>
        <div class="mt-2 text-right">
          <base-button
            type="primary"
            size="sm"
            :disabled="!newNote.trim() || loading"
            @click="addNote"
          >
            <span v-if="loading"><i class="el-icon-loading"></i></span>
            <span v-else>Add note</span>
          </base-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { formatGroupDate, formatGroupTime } from '@/plugins/dates';
import axios from 'axios';
import { API_LOCATION } from '@/api-config';
import { Auth } from 'aws-amplify';
import { Tooltip } from 'element-ui';

export default {
  name: 'NotesView',

  components: {
    [Tooltip.name]: Tooltip
  },

  props: {
    booking: { type: Object, default: () => ({}) },
    notes: { type: Array, default: () => [] },
    canManage: { type: Boolean, default: false },
    companyId: { type: [Number, String], default: null },
    companyGroupId: { type: [Number, String], default: null },
    dateFormat: { type: String, default: 'MM-DD-YYYY' },
    timeFormat: { type: String, default: '12h' },
    refreshData: { type: Function, default: null }
  },

  data() {
    return {
      loading: false,
      newNote: ''
    };
  },

  computed: {
    sortedNotes() {
      return [...this.notes].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    }
  },

  methods: {

    async getAuthToken() {
      const session = await Auth.currentSession();
      return session.getIdToken().getJwtToken();
    },

    getLocalDate(d) {
      return formatGroupDate(d);
    },

    getLocalTime(d) {
      return formatGroupTime(d);
    },

    async addNote() {
      if (!this.newNote.trim()) return;

      this.loading = true;
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/bookings/${this.booking.id}/notes`;
        const noteText = this.newNote.trim();

        await axios.post(url, { note: noteText }, config);
        this.newNote = '';

        this.$notify({
          type: 'success',
          message: 'Note added',
          verticalAlign: 'top',
          horizontalAlign: 'right'
        });

        if (this.refreshData) await this.refreshData();
      } catch (error) {
        console.error('Failed to add note:', error);
        this.$notify({
          type: 'danger',
          message: 'Failed to add note',
          verticalAlign: 'top',
          horizontalAlign: 'right'
        });
      } finally {
        this.loading = false;
      }
    },

    async toggleImportant(note) {
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/bookings/${this.booking.id}/notes/${note.id}`;
        await axios.put(url, { is_important: 1 }, config);
        if (this.refreshData) await this.refreshData();
      } catch (error) {
        console.error('Failed to update note:', error);
        this.$notify({
          type: 'danger',
          message: 'Failed to update note',
          verticalAlign: 'top',
          horizontalAlign: 'right'
        });
      }
    },

    async deleteNote(note) {
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/bookings/${this.booking.id}/notes/${note.id}`;

        await axios.delete(url, config);

        this.$notify({
          type: 'success',
          message: 'Note deleted',
          verticalAlign: 'top',
          horizontalAlign: 'right'
        });

        if (this.refreshData) await this.refreshData();
      } catch (error) {
        console.error('Failed to delete note:', error);
        this.$notify({
          type: 'danger',
          message: 'Failed to delete note',
          verticalAlign: 'top',
          horizontalAlign: 'right'
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.view-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.back-btn {
  width: 28px;
  height: 28px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: white;
  }
}

.view-title {
  font-size: 14px;
  font-weight: 600;
  color: white;
}

.view-body {
  padding: 16px 24px;
  overflow-y: auto;
  flex: 1;
}

.notes-list {
  max-height: 400px;
  overflow-y: auto;
  margin-bottom: 16px;
}

.note-card {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 10px;
  border-left: 3px solid transparent;

  &:last-child { margin-bottom: 0; }

  &--important {
    border-left-color: #ffd600;
    background: rgba(255, 214, 0, 0.08);
  }
}

.note-card__content {
  color: #fff;
  font-size: 13px;
  line-height: 1.5;
  margin: 0 0 10px 0;
  word-wrap: break-word;
  white-space: pre-wrap;
}

.note-card__footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.note-card__meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.note-card__author {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
}

.note-card__date {
  font-size: 11px;
}

.note-card__actions {
  display: flex;
  gap: 4px;
}

.note-card__action-btn {
  width: 28px;
  height: 28px;
  border: none;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    color: #fff;
  }

  &--active {
    color: #ffd600;
    cursor: default;
    &:hover { color: #ffd600; background: rgba(255, 255, 255, 0.08); }
  }

  &--delete:hover {
    background: rgba(253, 93, 147, 0.2);
    color: #fd5d93;
  }
}

.add-note-section {
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);

  &.no-border { border-top: none; padding-top: 0; }
}

.note-input-label {
  display: block;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 8px;
  text-transform: uppercase;
}

.note-textarea {
  width: 100%;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  padding: 10px 12px;
  color: #fff;
  font-size: 13px;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;

  &::placeholder { color: rgba(255, 255, 255, 0.3); }
  &:focus {
    outline: none;
    border-color: #e14eca;
  }
}
</style>

<style lang="scss">
.white-content {
  .notes-view .view-header {
    border-bottom-color: #e3e3e3;
  }

  .notes-view .back-btn {
    background: #f5f6fa;
    border-color: #e3e3e3;
    color: #9A9A9A;
    &:hover { background: #e9ecef; color: #344675; }
  }

  .notes-view .view-title {
    color: #344675;
  }

  .notes-view .note-card {
    background: #f5f6fa;
  }

  .notes-view .note-card--important {
    background: rgba(255, 214, 0, 0.15);
  }

  .notes-view .note-card__content {
    color: #344675;
  }

  .notes-view .note-card__author {
    color: #344675;
  }

  .notes-view .note-card__action-btn {
    background: #e9ebf0;
    color: #525f7f;
    &:hover { background: #d1d5db; color: #344675; }
  }

  .notes-view .note-card__action-btn--delete:hover {
    background: rgba(253, 93, 147, 0.15);
    color: #fd5d93;
  }

  .notes-view .add-note-section {
    border-top-color: #e3e3e3;
  }

  .notes-view .note-input-label {
    color: #525f7f;
  }

  .notes-view .note-textarea {
    background: #f5f6fa;
    border-color: #e3e3e3;
    color: #344675;
    &::placeholder { color: #9A9A9A; }
    &:focus { border-color: #e14eca; }
  }
}
</style>
